import { Fragment, FunctionComponent, useState } from 'react';
import {
    Box,
    Button,
    Divider,
    IconButton,
    SvgIcon,
    TextField,
    Typography,
} from '@mui/material';
import { Billing } from '@starjack/entities/billing';
import { formatStarjackDate } from '@/utils/time';
import { getBillingExcel } from './BillingsContent';
import { downloadContentGeneric } from '@/utils';
import { useGetAllLiftOperatorsQuery } from '@/api/v2/liftOperator';
import { usePostReleaseMutation } from '@starjack/entities/billing';
import { useNotification } from '@starjack/features/notifications';
import { useSplitLiftOperatorsBySales } from './hooks/useSplitLiftOperatorsBySales';
import { LiftOperator } from '@/models/liftoperator';
import { CardListRow } from '@/components/Card/CardListRow';
import DownloadIcon from '@mui/icons-material/Download';
import { LoadingContainer } from '@/components/Layout/Container';
import CloseIcon from '@mui/icons-material/Close';
import { AccordionCard } from '@/components/Accordion/AccordionCard';

type BillingDrawerContentProps = {
    billing: Billing;
    onClose: () => void;
};

export const BillingDrawerContent: FunctionComponent<
    BillingDrawerContentProps
> = ({ billing, onClose }) => {
    const { data: liftOperators = [], isFetching } =
        useGetAllLiftOperatorsQuery();

    const [postRelease] = usePostReleaseMutation();
    const { addNotification } = useNotification();
    const [filterValue, setFilterValue] = useState('');

    const [lOWithSellings, lOWithoutSellings] = useSplitLiftOperatorsBySales(
        liftOperators,
        billing,
        filterValue,
        isFetching
    );

    const onDownloadClick = async (liftOperatorId: string) => {
        const response = await getBillingExcel(billing.id, liftOperatorId);

        if (response)
            downloadContentGeneric(
                `Abrechnung ${formatStarjackDate(
                    billing.billingPeriodFrom
                )} - ${formatStarjackDate(billing.billingPeriodTo)}.xlsx`,
                response
            );
    };

    const releaseInvoices = () => {
        postRelease({ billingId: billing.id })
            .unwrap()
            .then(() => {
                addNotification({
                    message: 'Rechnungen erfolgreich freigegeben!',
                    type: 'success',
                });
                onClose();
            })
            .catch(() => {
                addNotification({
                    type: 'error',
                    message:
                        'Fehler beim Freigeben. Bitte später noch einmal versuchen',
                });
            });
    };

    return (
        <Box sx={{ width: '520px', height: '100%' }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    bgcolor: 'primary.dark',
                    p: 4,
                }}
            >
                <Typography
                    variant='h6'
                    sx={{
                        color: '#fff',
                    }}
                >
                    Abrechnung ({formatStarjackDate(billing.billingPeriodFrom)}{' '}
                    - {formatStarjackDate(billing.billingPeriodTo)})
                </Typography>

                <IconButton
                    sx={{
                        ml: 'auto',
                        color: '#fff',
                    }}
                    onClick={() => {
                        onClose();
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </Box>

            <Box
                sx={{
                    position: 'relative',
                    height: '100%',
                    width: '100%',
                }}
            >
                {isFetching ? (
                    <LoadingContainer cover />
                ) : (
                    <>
                        <Box
                            sx={{
                                p: 4,
                            }}
                        >
                            {!billing.releasedAt && (
                                <Button
                                    sx={{
                                        ml: 'auto',
                                        mb: 6,
                                        width: '100%',
                                    }}
                                    variant='contained'
                                    onClick={() => {
                                        releaseInvoices();
                                    }}
                                >
                                    Rechnungen freigeben
                                </Button>
                            )}

                            <TextField
                                sx={{
                                    mb: 3,
                                }}
                                onChange={(e) => {
                                    setFilterValue(e.target.value || '');
                                }}
                                label='Nach Bergbahn suchen'
                                fullWidth
                            />

                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 2,
                                }}
                            >
                                <AccordionContainer
                                    filterValue={filterValue}
                                    liftOperators={lOWithSellings}
                                    title='Bahnen mit Daten'
                                    onDownloadLink={onDownloadClick}
                                />

                                <AccordionContainer
                                    filterValue={filterValue}
                                    liftOperators={lOWithoutSellings}
                                    title='Bahnen ohne Daten'
                                    onDownloadLink={onDownloadClick}
                                />
                            </Box>
                        </Box>
                    </>
                )}
            </Box>
        </Box>
    );
};

type AccordionContainerProps = {
    filterValue: string;
    liftOperators: LiftOperator[];
    title: string;
    onDownloadLink: (liftOperatorId: string) => void;
};

const AccordionContainer: FunctionComponent<AccordionContainerProps> = ({
    filterValue,
    liftOperators,
    title,
    onDownloadLink,
}) => (
    <AccordionCard
        title={title}
        open={filterValue.length ? true : undefined}
        stayExpandedOnDetailsClick
    >
        {liftOperators.length === 0 && (
            <CardListRow>Keine Bergbahnen gefunden</CardListRow>
        )}
        {liftOperators.map((liftOperator, index) => {
            return (
                <Fragment key={liftOperator.id}>
                    {index !== 0 && (
                        <Divider
                            sx={{
                                width: '100%',
                            }}
                        />
                    )}
                    <CardListRow
                        onClick={() => {
                            onDownloadLink(liftOperator.id);
                        }}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            width: '100%',
                        }}
                        hoverable
                    >
                        <Typography
                            sx={{
                                width: '75%',
                            }}
                        >
                            {
                                liftOperators.find(
                                    (lo) => lo.id === liftOperator.id
                                ).name.de
                            }
                        </Typography>
                        <IconButton
                            sx={{
                                ml: 'auto',
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                onDownloadLink(liftOperator.id);
                            }}
                        >
                            <SvgIcon>
                                <DownloadIcon />
                            </SvgIcon>
                        </IconButton>
                    </CardListRow>
                </Fragment>
            );
        })}
    </AccordionCard>
);
